import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion, AnimatePresence } from "framer-motion";

// Context
const HoverVideoPlayerContext = createContext(null);

const useHoverVideoPlayer = () => {
  const context = useContext(HoverVideoPlayerContext);
  if (!context) {
    throw new Error(
      "useHoverVideoPlayer must be used within a HoverVideoPlayer"
    );
  }
  return context;
};

// Helper functions
function debounce(func, wait) {
  let timeoutId = null;
  const debouncedFn = (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), wait);
  };
  debouncedFn.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };
  return debouncedFn;
}

function isVimeoUrl(url) {
  return url.includes("player.vimeo.com/video/") || url.includes("vimeo.com/");
}

function loadVimeoSDK() {
  return new Promise((resolve, reject) => {
    if (window.Vimeo) {
      resolve(window.Vimeo);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.async = true;
    script.onload = () => resolve(window.Vimeo);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// Simple Button Component
const Button = ({ children, onClick, className = "", ariaLabel, ...props }) => {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-md p-2 hover:bg-black/20 transition-colors ${className}`}
      aria-label={ariaLabel}
      {...props}
    >
      {children}
    </button>
  );
};

// Simple Slider Component
const Slider = ({ value, min, max, step, onValueChange, className = "", ariaLabel }) => {
  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    onValueChange([newValue]);
  };

  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value[0]}
      onChange={handleChange}
      className={`h-2 bg-white/20 rounded-lg appearance-none cursor-pointer ${className}`}
      style={{
        background: `linear-gradient(to right, white 0%, white ${(value[0] / max) * 100}%, rgba(255,255,255,0.2) ${(value[0] / max) * 100}%, rgba(255,255,255,0.2) 100%)`
      }}
      aria-label={ariaLabel}
    />
  );
};

// Icon Components (simple SVG)
const Play = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const Pause = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
  </svg>
);

const Volume2 = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

const VolumeX = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
    <path d="M16.5 12l2.5-2.5-1.5-1.5L15 10.5 12.5 8 11 9.5 13.5 12 11 14.5l1.5 1.5L15 13.5l2.5 2.5 1.5-1.5-2.5-2.5z" />
  </svg>
);

const Maximize = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
);

const Minimize = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
  </svg>
);

// Main Component
const HoverVideoPlayer = ({
  videoSrc,
  thumbnailSrc,
  hoverOverlay,
  pausedOverlay,
  loadingOverlay,
  playbackStartDelay = 0,
  restartOnPaused = false,
  unloadVideoOnPaused = false,
  playbackRangeStart,
  playbackRangeEnd,
  muted: initialMuted = false,
  loop = true,
  preload = "metadata",
  className = "",
  style = {},
  onHoverStart,
  onHoverEnd,
  enableControls = false,
  cropTop = 0,
  cropBottom = 0,
  isVimeo = false,
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const progressIntervalRef = useRef(null);

  const [state, setState] = useState({
    isHovering: false,
    isPlaying: false,
    isLoading: false,
    progress: 0,
    muted: initialMuted,
    volume: 1,
    isPiP: false,
    isMobile: false,
    controlsVisible: false,
    showThumbnail: true,
    isInView: false,
  });

  // Mobile detection
  const checkMobile = useMemo(
    () =>
      debounce(() => {
        setState((prev) => ({ ...prev, isMobile: window.innerWidth <= 768 }));
      }, 200),
    []
  );

  // Event Handlers
  const handleTouchStart = useCallback(() => {
    if (state.isMobile) {
      setState((prev) => ({ ...prev, controlsVisible: !prev.controlsVisible }));
    }
  }, [state.isMobile]);

  const handleHoverStart = useCallback(() => {
    if (!state.isMobile) {
      setState((prev) => ({ ...prev, isHovering: true }));
      onHoverStart?.();
    }
  }, [state.isMobile, onHoverStart]);

  const handleHoverEnd = useCallback(() => {
    if (!state.isMobile) {
      setState((prev) => ({ ...prev, isHovering: false }));
      onHoverEnd?.();
    }
  }, [state.isMobile, onHoverEnd]);

  // Video Playback Controls
  const playVideo = useCallback(() => {
    if (!videoRef.current || !state.isInView) return;

    const video = videoRef.current;
    setState((prev) => ({ ...prev, isLoading: true }));

    const attemptPlay = () => {
      video
        .play()
        .then(() => {
          setState((prev) => ({
            ...prev,
            isLoading: false,
            isPlaying: true,
            showThumbnail: false,
          }));
        })
        .catch((error) => {
          if (error.name === "NotAllowedError") {
            setState((prev) => ({
              ...prev,
              isLoading: false,
              showThumbnail: true,
            }));
          } else if (error.name !== "AbortError") {
            console.error("HoverVideoPlayer: Playback error:", error);
            setState((prev) => ({
              ...prev,
              isLoading: false,
              showThumbnail: true,
            }));
          }
        });
    };

    if (video.readyState >= 3) {
      attemptPlay();
    } else {
      const handleCanPlay = () => {
        video.removeEventListener("canplay", handleCanPlay);
        attemptPlay();
      };
      video.addEventListener("canplay", handleCanPlay);
    }
  }, [state.isInView]);

  const pauseVideo = useCallback(() => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    setState((prev) => ({
      ...prev,
      isPlaying: false,
      showThumbnail: true,
    }));
    if (restartOnPaused) {
      videoRef.current.currentTime = playbackRangeStart || 0;
    }
  }, [restartOnPaused, playbackRangeStart]);

  // Effects
  useEffect(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
      checkMobile.cancel();
    };
  }, [checkMobile]);

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isNowInView = entry.isIntersecting;
          setState((prev) => ({ ...prev, isInView: isNowInView }));
          if (!isNowInView) {
            pauseVideo();
          }
        });
      },
      {
        root: null,
        rootMargin: "50px",
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [pauseVideo]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !state.isInView) return;

    const updateProgress = () => {
      if (video.duration) {
        const progress = (video.currentTime / video.duration) * 100;
        setState((prev) => ({ ...prev, progress }));
      }
    };

    const handlers = {
      loadstart: () => {
        setState((prev) => ({ ...prev, isLoading: true }));
      },
      loadeddata: () => {
        setState((prev) => ({ ...prev, isLoading: false }));
        // Start alleen afspelen bij hover of mobile controls
        if (state.isHovering || state.controlsVisible) {
          playVideo();
        }
      },
      playing: () => {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          isPlaying: true,
          showThumbnail: false,
        }));
      },
      pause: () => {
        setState((prev) => ({
          ...prev,
          isPlaying: false,
          showThumbnail: true,
        }));
      },
      timeupdate: updateProgress,
      error: () => {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          showThumbnail: true,
        }));
      },
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      video.addEventListener(event, handler);
    });

    // Progress interval
    progressIntervalRef.current = setInterval(updateProgress, 100);

    if (!video.src) {
      video.src = videoSrc;
      video.load();
    }

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        video.removeEventListener(event, handler);
      });
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [
    state.isInView,
    state.isHovering,
    state.controlsVisible,
    playVideo,
    videoSrc,
  ]);

  // Playback control effect - Normale logica: hover = play, geen hover = pause
  useEffect(() => {
    if (!state.isInView) return;

    let playbackTimeout;
    if (state.isMobile) {
      if (state.controlsVisible) {
        playVideo();
      } else {
        pauseVideo();
      }
    } else if (state.isHovering) {
      // Bij hover: speel de video
      playbackTimeout = setTimeout(() => {
        playVideo();
      }, playbackStartDelay);
    } else {
      // Bij geen hover: pauzeer de video
      pauseVideo();
    }

    return () => {
      if (playbackTimeout) {
        clearTimeout(playbackTimeout);
      }
    };
  }, [
    state.isInView,
    state.isMobile,
    state.controlsVisible,
    state.isHovering,
    playVideo,
    pauseVideo,
    playbackStartDelay,
  ]);

  // Volume effect
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = state.muted;
    video.volume = state.volume;
  }, [state.muted, state.volume]);

  // Control handlers
  const togglePlayPause = useCallback(() => {
    if (state.isPlaying) {
      pauseVideo();
    } else {
      playVideo();
    }
  }, [state.isPlaying, pauseVideo, playVideo]);

  const toggleMute = useCallback(() => {
    setState((prev) => ({ ...prev, muted: !prev.muted }));
  }, []);

  const togglePiP = useCallback(async () => {
    if (!document.pictureInPictureEnabled) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setState((prev) => ({ ...prev, isPiP: false }));
      } else if (videoRef.current) {
        await videoRef.current.requestPictureInPicture();
        setState((prev) => ({ ...prev, isPiP: true }));
      }
    } catch (error) {
      console.error("PiP error:", error);
    }
  }, []);

  const setVolume = useCallback((value) => {
    setState((prev) => ({ ...prev, volume: value }));
  }, []);

  const setProgress = useCallback((value) => {
    setState((prev) => ({ ...prev, progress: value }));
    if (videoRef.current && videoRef.current.duration) {
      const newTime = (value / 100) * videoRef.current.duration;
      videoRef.current.currentTime = newTime;
    }
  }, []);

  // Context value
  const contextValue = useMemo(
    () => ({
      isPlaying: state.isPlaying,
      isHovering: state.isHovering,
      isLoading: state.isLoading,
      progress: state.progress,
      volume: state.volume,
      muted: state.muted,
      isPiP: state.isPiP,
      isMobile: state.isMobile,
      controlsVisible: state.controlsVisible,
      videoRef,
      togglePlay: togglePlayPause,
      toggleMute,
      togglePiP,
      setVolume,
      setProgress,
      cropTop,
      cropBottom,
      thumbnailSrc,
    }),
    [
      state,
      togglePlayPause,
      toggleMute,
      togglePiP,
      setVolume,
      setProgress,
      cropTop,
      cropBottom,
      thumbnailSrc,
    ]
  );

  return (
    <HoverVideoPlayerContext.Provider value={contextValue}>
      <motion.div
        ref={containerRef}
        className={`relative overflow-hidden group cursor-pointer ${className}`}
        style={{
          ...style,
          paddingTop: `${cropTop}%`,
          paddingBottom: `${cropBottom}%`,
        }}
        onMouseEnter={handleHoverStart}
        onMouseLeave={handleHoverEnd}
        onTouchStart={handleTouchStart}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 0.9 }}
        whileHover={{ scale: 1.0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
      >
        {/* Video Element */}
        {state.isInView && (
          <HoverVideoPlayerVideo
            src={videoSrc}
            unloadVideoOnPaused={unloadVideoOnPaused}
            loop={loop}
            preload={preload}
            isVimeo={isVimeo}
          />
        )}

        {/* Thumbnail */}
        {thumbnailSrc && (state.showThumbnail || !state.isInView) && (
          <HoverVideoPlayerThumbnail src={thumbnailSrc} />
        )}

        {/* Overlays */}
        {pausedOverlay && (
          <HoverVideoPlayerPausedOverlay>
            {pausedOverlay}
          </HoverVideoPlayerPausedOverlay>
        )}

        {loadingOverlay && (
          <HoverVideoPlayerLoadingOverlay>
            {loadingOverlay}
          </HoverVideoPlayerLoadingOverlay>
        )}

        {hoverOverlay && !state.isMobile && (
          <HoverVideoPlayerHoverOverlay>
            {hoverOverlay}
          </HoverVideoPlayerHoverOverlay>
        )}

        {/* Controls */}
        {enableControls && (
          <HoverVideoPlayerControls>
            <div className="flex items-center space-x-2">
              <HoverVideoPlayerPlayPauseButton />
              <HoverVideoPlayerVolumeControl />
              <HoverVideoPlayerPiPButton />
            </div>
            <HoverVideoPlayerProgressBar />
          </HoverVideoPlayerControls>
        )}
      </motion.div>
    </HoverVideoPlayerContext.Provider>
  );
};

// Video component
const HoverVideoPlayerVideo = ({ src, unloadVideoOnPaused, loop, preload, isVimeo }) => {
  const { videoRef, muted, cropTop, cropBottom, isHovering } = useHoverVideoPlayer();
  const isVimeoVideo = isVimeo || isVimeoUrl(src);
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    if (!isVimeoVideo || !containerRef.current) return;

    const videoId = src.split("/").pop() || "";
    let player = null;

    loadVimeoSDK()
      .then((Vimeo) => {
        if (!containerRef.current) return;
        player = new Vimeo.Player(containerRef.current, {
          id: videoId,
          autopause: false,
          muted,
          loop,
          responsive: true,
          controls: false,
          autoplay: false,
          volume: 1,
        });

        playerRef.current = player;

        player.ready().then(() => {
          if (isHovering) {
            player?.play();
          }
        });

        if (isHovering) {
          player?.play().catch(console.error);
        } else {
          player?.pause().catch(console.error);
        }
      })
      .catch((error) => {
        console.error("Failed to load Vimeo SDK:", error);
      });

    return () => {
      if (player) {
        player.destroy();
        playerRef.current = null;
      }
    };
  }, [isVimeoVideo, src, muted, loop, isHovering]);

  if (isVimeoVideo) {
    return <div ref={containerRef} className="absolute inset-0" />;
  }

  return (
    <video
      ref={videoRef}
      className="absolute inset-0 w-full h-full object-cover"
      style={{
        top: `-${cropTop}%`,
        bottom: `-${cropBottom}%`,
        height: `calc(100% + ${cropTop + cropBottom}%)`,
      }}
      muted={muted}
      loop={loop}
      preload={preload}
      playsInline
      crossOrigin="anonymous"
      aria-label="Video player"
      role="application"
    />
  );
};

// Thumbnail component
const HoverVideoPlayerThumbnail = ({ src }) => {
  const { cropTop, cropBottom, isHovering, isLoading, isPlaying } = useHoverVideoPlayer();

  if ((isPlaying || isHovering) && !isLoading) return null;

  return (
    <div
      className="absolute inset-0 w-full h-full"
      style={{
        top: `-${cropTop}%`,
        bottom: `-${cropBottom}%`,
        height: `calc(100% + ${cropTop + cropBottom}%)`,
      }}
    >
      <img
        src={src}
        alt="Video thumbnail"
        className="w-full h-full object-cover"
      />
    </div>
  );
};

// Controls component
const HoverVideoPlayerControls = ({ children }) => {
  const { isHovering, isMobile, controlsVisible } = useHoverVideoPlayer();
  const shouldShowControls = isMobile ? controlsVisible : isHovering;

  return (
    <motion.div
      className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent"
      initial={{ opacity: 0, y: 20 }}
      animate={{
        opacity: shouldShowControls ? 1 : 0,
        y: shouldShowControls ? 0 : 20,
      }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
};

// Hover overlay
const HoverVideoPlayerHoverOverlay = ({ children }) => {
  const { isHovering, isLoading } = useHoverVideoPlayer();

  return (
    <AnimatePresence>
      {isHovering && !isLoading && (
        <motion.div
          className="absolute inset-0 z-30 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Paused overlay
const HoverVideoPlayerPausedOverlay = ({ children }) => {
  const { isPlaying, isLoading, isHovering } = useHoverVideoPlayer();

  return (
    <AnimatePresence>
      {!isPlaying && !isLoading && !isHovering && (
        <motion.div
          className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Loading overlay
const HoverVideoPlayerLoadingOverlay = ({ children }) => {
  const { isLoading } = useHoverVideoPlayer();

  return (
    <AnimatePresence mode="wait">
      {isLoading && (
        <motion.div
          className="absolute inset-0 z-20 flex items-center justify-center bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Play/Pause button
const HoverVideoPlayerPlayPauseButton = () => {
  const { isPlaying, togglePlay } = useHoverVideoPlayer();

  return (
    <Button
      onClick={togglePlay}
      className="text-white hover:text-white/80"
      ariaLabel={isPlaying ? "Pause video" : "Play video"}
    >
      {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
    </Button>
  );
};

// Volume control
const HoverVideoPlayerVolumeControl = () => {
  const { muted, toggleMute, volume, setVolume } = useHoverVideoPlayer();

  return (
    <div className="flex items-center space-x-2">
      <Button
        onClick={toggleMute}
        className="text-white hover:text-white/80"
        ariaLabel={muted ? "Unmute" : "Mute"}
      >
        {muted ? (
          <VolumeX className="h-6 w-6" />
        ) : (
          <Volume2 className="h-6 w-6" />
        )}
      </Button>
      <Slider
        className="w-24"
        value={[muted ? 0 : volume * 100]}
        min={0}
        max={100}
        step={1}
        onValueChange={(value) => setVolume(value[0] / 100)}
        ariaLabel="Volume"
      />
    </div>
  );
};

// Progress bar
const HoverVideoPlayerProgressBar = () => {
  const { progress, setProgress } = useHoverVideoPlayer();

  return (
    <Slider
      className="mt-2 w-full"
      value={[progress]}
      min={0}
      max={100}
      step={0.1}
      onValueChange={(value) => setProgress(value[0])}
      ariaLabel="Video progress"
    />
  );
};

// PiP button
const HoverVideoPlayerPiPButton = () => {
  const { isPiP, togglePiP } = useHoverVideoPlayer();

  return (
    <Button
      onClick={togglePiP}
      className="text-white hover:text-white/80"
      ariaLabel={isPiP ? "Exit picture in picture" : "Enter picture in picture"}
    >
      {isPiP ? (
        <Minimize className="h-6 w-6" />
      ) : (
        <Maximize className="h-6 w-6" />
      )}
    </Button>
  );
};

export default HoverVideoPlayer;
